/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import PropTypes from 'util/PropTypes';
import StringResources from 'nls/StringResources';
import './showByDialog.scss';

import { Label, AutoCompleteInput, SearchInput, List, ListItem, FlexItem, TruncatedText } from '@ba/ba-ui-carbon-toolkit';
import { ShapingPickTree } from 'ca-modeller/shaping';

const moser = require('bi/moser/moser.min');
const moserjs = moser.default;

class ShowByDialog extends Component {
	constructor(props) {
		super(props);
		this.state = {
			searchResult: '',
			searchString: '',
			mounted: false,
			isSearchFocused: !props.recommendedColumns.length
		};
		this.contextColumns = props.contextColumns;
		this.contextColumnsMoser = this.contextColumns.map(col => col.moserObject);
		this.selectionHandler = props.eventHandler;
		this.recommendedColumns = props.recommendedColumns;
		this.recommendedColumnsMoser = this.recommendedColumns.map(col => col.moserObject);
		this.searchPlaceHolder = StringResources.get('searchAll');
		this.recommendedColumnsLabel = StringResources.get('recommendedColumns');
		this.allColumnsLabel = StringResources.get('allColumns');
		this.searchRef = null;
	}

	componentDidMount() {
		// MUI has an issue with the container height and its virtualized list
		// they need our component to render first so they can calculate the height needed for the tree
		setTimeout(() => this.setState({ mounted: true }), 0);
		this.moserTreeMapRecommended = new Map();
		this.moserTreeMapSearch = new Map();
	}

	componentWillUnmount() {
		this.moserTreeMapRecommended = null;
		this.moserTreeMapSearch = null;
	}

	render() {
		if (this.props.shapingHelper) {
			return (
				<div className="showByDialogAmb ba-theme-waca">
					<SearchInput
						className="searchBar"
						value={this.state.searchString}
						placeholder={this.searchPlaceHolder}
						onChange={this._onInputChange}
						fullWidth="true"
						onFocus={this._onFocus}
						onBlur={this._onBlur}
						ref={r => this.searchRef = r}
					/>
					{ this._getTreeListComponent() }
				</div>
			);
		}

		return (
			<div className="showByDialog ba-theme-waca">
				<AutoCompleteInput
					values={this.contextColumns}
					value={this.state.searchString}
					placeholder={this.searchPlaceHolder}
					onChange={this._onInputChange}
					onSelectionChange={this._onSelectionChange}
					onValueMatch={this._onValueMatch}
					fullWidth="true"
				/>
				{ this._getListComponent() }
			</div>
		);
	}

	_onFocus = () => {
		this.setState({ isSearchFocused: true });
	}

	_onBlur = () => {
		this.setState({ isSearchFocused: false });
	}

	_onInputChange = (value) => {
		if (!value) {
			// The icon 'x' is clicked to clear search input, make sure search result is also cleared out
			this.setState({ searchResult: '' });
		}
		this.setState({ searchString: value });
	}

	_onValueMatch = (values) => {
		this.setState({ searchResult: values });
	}

	_onSelectionChange = (selection) => {
		this.selectionHandler({
			id: selection.value,
			label: selection.label
		});
	}

	/**
	* Display the recommended columns list only when no search result
	*/
	_getListComponent = () => {
		if (this.recommendedColumns && this.recommendedColumns.length && !this.state.searchResult && !this.state.searchString) {
			return (
				<div>
					<div className="columnsListLabel">
						<Label label={this.recommendedColumnsLabel}/>
					</div>
					<div className="columnsListContainer">
						<List
							length={this.recommendedColumns.length}
							itemRenderer={this._itemRenderer}
							selectionMode="single"
							onSelectionChange={this._onListSelection}
							tabIndex={0}
						/>
					</div>
				</div>
			);
		} else {
			return null;
		}
	}

	_getTreeListComponent = () => {
		const { shapingHelper } = this.props;

		let props = {};
		if (!this.state.isSearchFocused && this.state.searchString === '') {
			props = {
				moserObjectFilter: this.recommendedPredicate
			};
		} else if (this.searchRef) {
			this.searchRef.focus();

			props = {
				moserObjectFilter: this.searchPredicate
			};
		}

		let tree = null;
		if (this.state.mounted) {
			tree = (
				<ShapingPickTree
					filter={this.state.searchString}
					onSelect={this._onSelect}
					{...props}
				/>
			);
		}

		return (
			<shapingHelper.ShapingProvider>
				<div>
					<div className="columnsListLabel">
						<Label label={this.state.isSearchFocused || this.state.searchString !== '' ? this.allColumnsLabel : this.recommendedColumnsLabel}/>
					</div>
					<div className="columnsTreeContainer">
						{tree}
					</div>
				</div>
			</shapingHelper.ShapingProvider>
		);
	}

	// Returns true when the object should be shown in the tree, false otherwise
	searchPredicate = (moserObject) => {
		if (moserObject.getObjectType() === moserjs.MoserObjectTypes.QUERY_ITEM) {
			return this.contextColumnsMoser.indexOf(moserObject) >= 0;
		}

		if (this.moserTreeMapSearch.has(moserObject)) {
			return this.moserTreeMapSearch.get(moserObject);
		}

		const hasSearchMembers = moserjs.ModuleUtils.getTreeChildren(moserObject).some(child => this.searchPredicate(child));

		this.moserTreeMapSearch.set(moserObject, hasSearchMembers);

		return hasSearchMembers;
	}

	// Returns true when the object should be shown in the tree, false otherwise
	recommendedPredicate = (moserObject) => {
		if (moserObject.getObjectType() === moserjs.MoserObjectTypes.QUERY_ITEM) {
			return this.recommendedColumnsMoser.indexOf(moserObject) >= 0;
		}

		if (this.moserTreeMapRecommended.has(moserObject)) {
			return this.moserTreeMapRecommended.get(moserObject);
		}

		const hasRecommendedMembers = moserjs.ModuleUtils.getTreeChildren(moserObject).some(child => this.recommendedPredicate(child));

		this.moserTreeMapRecommended.set(moserObject, hasRecommendedMembers);

		return hasRecommendedMembers;
	}

	_onSelect = (selected) => {
		if (selected.getObjectType() === moserjs.MoserObjectTypes.QUERY_ITEM) {
			this.selectionHandler({
				id: selected.idForExpression,
				label: selected.label
			});
		}
	}

	_onListSelection = (event) => {
		const selection = this.recommendedColumns[event.index];
		this.selectionHandler(selection);
	}

	_itemRenderer = (index) => {
		const item = this.recommendedColumns[index];
		return (
			<ListItem data-index={index} selectable="true">
				<FlexItem grow>
					<TruncatedText value={item.label} />
				</FlexItem>
			</ListItem>
		);
	}
}

ShowByDialog.propTypes = {
	contextColumns: PropTypes.array,
	eventHandler: PropTypes.func,
	recommendedColumns: PropTypes.array,
	shapingHelper: PropTypes.object
};

export default ShowByDialog;



// WEBPACK FOOTER //
// ./src/js/components/showBy/ShowByDialog.js